import { z } from 'zod'
import { ExternalStorageAccountStatusSchema, ExternalStorageProviderSchema } from '@/settings/schemas/external-storage-schema'

export const ExternalFileItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  is_folder: z.boolean().default(false),
  mime_type: z.string().nullable().optional(),
  size: z.number().nullable().optional(),
  etag: z.string().nullable().optional(),
  modified_at: z.string().nullable().optional(),
  web_url: z.string().nullable().optional(),
  external_sync_matter_id: z.string().uuid().nullable().optional(),
  external_sync_matter_name: z.string().nullable().optional(),
})
export type ExternalFileItem = z.infer<typeof ExternalFileItemSchema>

export const SharePointSiteSearchResponseSchema = z.object({
  sites: z.array(ExternalFileItemSchema.extend({ web_url: z.string() })),
  more_results_available: z.boolean(),
})
export type SharePointSiteSearchResponse = z.infer<typeof SharePointSiteSearchResponseSchema>

export const ExternalFilesListResponseSchema = z.object({
  account_id: z.string().uuid(),
  provider: ExternalStorageProviderSchema,
  folder_id: z.string().nullable(),
  items: z.array(ExternalFileItemSchema),
})
export type ExternalFilesListResponse = z.infer<typeof ExternalFilesListResponseSchema>

const ExternalStorageFileImportStateEnumSchema = z.enum(['PENDING', 'IMPORTING', 'PENDING_FINALIZE', 'IMPORTED', 'FAILED'])
export const ExternalStorageFileImportStateSchema = z.preprocess(
  (value) => (typeof value === 'string' ? value.toUpperCase() : value),
  ExternalStorageFileImportStateEnumSchema
)
export type ExternalStorageFileImportState = z.infer<typeof ExternalStorageFileImportStateSchema>

export const ExternalFolderLinkMetadataSchema = z.object({
  id: z.string().uuid(),
  provider: ExternalStorageProviderSchema,
  is_externally_managed: z.boolean().default(true),
  external_folder_name: z.string().nullable(),
  etag: z.string().nullable(),
  source_modified_at: z.string().nullable(),
  tombstoned: z.boolean(),
  updated_at: z.string(),
})
export type ExternalFolderLinkMetadata = z.infer<typeof ExternalFolderLinkMetadataSchema>

export const ExternalFileLinkMetadataSchema = z.object({
  id: z.string().uuid(),
  provider: ExternalStorageProviderSchema,
  external_file_id: z.string().optional(),
  is_externally_managed: z.boolean().default(true),
  external_file_name: z.string().nullable(),
  etag: z.string().nullable(),
  source_modified_at: z.string().nullable(),
  import_state: ExternalStorageFileImportStateSchema,
  tombstoned: z.boolean(),
  updated_at: z.string(),
})
export type ExternalFileLinkMetadata = z.infer<typeof ExternalFileLinkMetadataSchema>

export const ImportFolderResponseSchema = z.object({
  external_sync_mapping_id: z.string().uuid(),
  user_folder_id: z.string().uuid(),
  created_mapping: z.boolean(),
  observed_files_count: z.number(),
  created_file_links_count: z.number(),
  observed_folders_count: z.number(),
})
export type ImportFolderResponse = z.infer<typeof ImportFolderResponseSchema>

export const MatterExternalSyncMappingPublicReadSchema = z.object({
  id: z.string().uuid(),
  account_id: z.string().uuid(),
  connected_by_user_id: z.string(),
  account_display_email: z.string().nullable(),
  account_status: ExternalStorageAccountStatusSchema,
  matter_id: z.string().uuid(),
  user_folder_id: z.string().uuid(),
  provider: ExternalStorageProviderSchema,
  external_folder_id: z.string(),
  external_folder_name: z.string().nullable(),
  external_folder_path: z.string().nullable(),
  status: z.enum(['active', 'paused', 'removed']),
  last_synced_at: z.string().nullable(),
  failure_message: z.string().nullable().default(null),
  // Older backend responses may omit this; clients then fall back to last_synced_at.
  has_pending_files: z.boolean().optional(),
})
export type MatterExternalSyncMappingPublicRead = z.infer<typeof MatterExternalSyncMappingPublicReadSchema>

export const MatterExternalSyncMappingResponseSchema = z.object({
  mapping: MatterExternalSyncMappingPublicReadSchema.nullable(),
})
export type MatterExternalSyncMappingResponse = z.infer<typeof MatterExternalSyncMappingResponseSchema>

export const MatterExternalSyncMappingDisconnectPreviewSchema = z.object({
  file_count: z.number().int().nonnegative(),
})
export type MatterExternalSyncMappingDisconnectPreview = z.infer<typeof MatterExternalSyncMappingDisconnectPreviewSchema>
